
































import { computed, defineComponent, ref } from "@vue/composition-api";
import { PayeeInformation } from "@scrile/api-provider/dist/api/PayeeInformationProvider";
import { PayoutRequestSettings } from "@scrile/api-provider/dist/api/PayoutRequestsProvider";
import { PayoutSystemSettings } from "@scrile/api-provider/dist/api/PayoutSystemSettingsProvider";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { handleRequestError } from "@/lib/handleRequestError";
import { $t } from "@/lib/i18nHelpers";
import { formatMoneyString, getCurrencyCode, getCurrencySymbol } from "@/lib/moneyHelpers";
import config from "@/config";
import { ERRORS } from "@/const";
import BaseButton from "@/components/BaseButton";
import CreatePayoutRequestModal from "./CreatePayoutRequestModal";

export default defineComponent({
  name: "AddNewRequest",
  components: { BaseButton, CreatePayoutRequestModal },
  props: {
    isEmptyRequests: { type: Boolean, default: false },
    primaryBalance: { type: Number, required: true },
  },
  setup(props, { emit }) {
    const { authUserStrict } = useAuthUser();
    const isShowModal = ref(false);
    const loading = ref(false);
    const inputValue = ref("");
    const currencySymbol = $t(getCurrencySymbol(getCurrencyCode()));
    const requestErrorValue = ref("");
    const allowSend = computed(() => {
      return inputValue.value.length !== 0 && +inputValue.value > 0;
    });

    const requestSettings = ref<PayoutRequestSettings | null>(null);
    const payeeInfo = ref<PayeeInformation | null>(null);
    const payoutSystemSettings = ref<PayoutSystemSettings[]>([]);
    const getOwnRequestSettings = async () => {
      await Promise.all([
        providers.PayoutRequestsProvider.getSettings({
          data: { userIds: [authUserStrict.value.id] },
        }).then(r => (requestSettings.value = r.find(i => i.userId === authUserStrict.value.id) ?? null)),
        providers.PayeeInformationProvider.find({ id: authUserStrict.value.id }).then(r => (payeeInfo.value = r)),
        providers.PayoutSystemSettingsProvider.findAll().then(r => (payoutSystemSettings.value = r)),
      ]);
    };
    getOwnRequestSettings();

    const payoutSystemName = computed(
      () => payoutSystemSettings.value.find(system => system.systemName === payeeInfo.value?.payoutSystem) || null
    );

    const commission = computed(() => {
      const amount = Number(inputValue.value);
      const percentCommission = payoutSystemName.value?.percentCommission ?? 0;
      const flatCommission = payoutSystemName.value?.flatCommission ?? 0;
      return isNaN(amount) ? 0 : (amount / 100) * percentCommission + flatCommission;
    });

    const subText = computed(() => {
      if (requestErrorValue.value) {
        return $t(requestErrorValue.value);
      }

      return $t("Minimum withdrawal amount is {0}", [
        formatMoneyString(requestSettings.value?.minimumAmount ?? 0, {
          minimumFractionDigits: config.EXTENDED_FRACTIONAL_PART_LENGTH,
        }),
      ]);
    });

    const onCreateRequest = async () => {
      if (!allowSend.value) return;
      try {
        loading.value = true;
        providers.PayoutRequestsProvider.setReceiveErrors(false);
        const r = await providers.PayoutRequestsProvider.create({
          data: {
            amount: +inputValue.value,
            userId: authUserStrict.value.id,
          },
        });
        inputValue.value = "";
        emit("createRequest", r);
      } catch (e) {
        handleRequestError(e, [
          {
            comparator: e =>
              e.details?.messageTemplate === ERRORS.PAYOUT_FIELD_AMOUNT ||
              e.details?.messageTemplate === ERRORS.PAYOUT_MIN_AMOUNT,
            onSuccessCB: e => (requestErrorValue.value = e.message ?? "Error"),
          },
        ]);
      } finally {
        loading.value = false;
      }
    };

    const onCloseModal = () => {
      inputValue.value = "";
      isShowModal.value = false;
      requestErrorValue.value = "";
    };

    const onAvailableAmount = () => {
      inputValue.value = props.primaryBalance.toString();
      requestErrorValue.value = "";
    };

    const onInput = (v: string) => {
      const value = +v;
      requestErrorValue.value = "";
      if (isNaN(value) || value === 0) {
        inputValue.value = "";
      } else {
        inputValue.value = value.toString();
      }
    };

    return {
      loading,
      isShowModal,
      inputValue,
      currencySymbol,
      requestSettings,
      subText,
      commission,
      requestErrorValue,
      onCreateRequest,
      onCloseModal,
      onAvailableAmount,
      onInput,
    };
  },
});
